(() => {
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const announceGlobal = (message) => {
    const node = qs('[data-aph-global-status]');
    if (!node) return;
    node.textContent = '';
    window.setTimeout(() => { if (node.isConnected) node.textContent = message || ''; }, 10);
  };
  const body = document.body;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = Boolean(window.Shopify?.designMode);
  if (designMode) body?.classList.add('aph-design-mode');
  const respectsReducedMotion = () => Boolean(body?.classList.contains('aph-respect-reduced-motion') && reducedMotion.matches && !designMode);
  const motionAllowed = () => !respectsReducedMotion();
  const runIdle = (callback, timeout = 1200) => {
    if (typeof window.requestIdleCallback === 'function') return window.requestIdleCallback(callback, { timeout });
    return window.setTimeout(callback, Math.min(timeout, 320));
  };

  const setOverlayState = () => {
    const dialogOpen = qsa('dialog[open]').length > 0;
    const mobileOpen = body.classList.contains('aph-menu-open');
    body.classList.toggle('aph-overlay-open', dialogOpen || mobileOpen);
  };

  const openDialog = (dialog, opener = document.activeElement) => {
    if (!dialog) return;
    qsa('dialog[open]').forEach((other) => {
      if (other === dialog) return;
      if (typeof other.close === 'function') other.close();
      else other.removeAttribute('open');
    });
    dialog._adReturnFocus = opener instanceof HTMLElement ? opener : null;
    if (!dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }
    setOverlayState();
  };

  const closeDialog = (dialog, restoreFocus = true) => {
    if (!dialog) return;
    const returnFocus = dialog._adReturnFocus;
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');
    setOverlayState();
    if (restoreFocus && returnFocus?.isConnected) window.setTimeout(() => returnFocus.focus(), 0);
  };

  const initNavigation = (scope = document) => {
    qsa('[data-aph-nav-toggle]', scope).forEach((button) => {
      if (button.dataset.aphBound === 'true') return;
      button.dataset.aphBound = 'true';
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        const item = button.closest('.aph-nav__item');
        const open = item?.classList.contains('is-open');
        qsa('.aph-nav__item.is-open').forEach((other) => {
          if (other !== item) {
            other.classList.remove('is-open');
            qs('[data-aph-nav-toggle]', other)?.setAttribute('aria-expanded', 'false');
          }
        });
        item?.classList.toggle('is-open', !open);
        button.setAttribute('aria-expanded', String(!open));
      });
    });

    if (document.documentElement.dataset.aphNavOutsideBound !== 'true') {
      document.documentElement.dataset.aphNavOutsideBound = 'true';
      document.addEventListener('click', (event) => {
        if (event.target.closest('.aph-nav__item')) return;
        qsa('.aph-nav__item.is-open').forEach((item) => {
          item.classList.remove('is-open');
          qs('[data-aph-nav-toggle]', item)?.setAttribute('aria-expanded', 'false');
        });
      });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        qsa('.aph-nav__item.is-open').forEach((item) => {
          item.classList.remove('is-open');
          const toggle = qs('[data-aph-nav-toggle]', item);
          toggle?.setAttribute('aria-expanded', 'false');
          toggle?.focus();
        });
        qsa('dialog[open]').forEach((dialog) => closeDialog(dialog));
        closeMobilePanel();
      });
    }
  };

  const closeMobilePanel = () => {
    const dialog = qs('[data-aph-mobile-dialog]');
    if (!dialog?.open) return;
    closeDialog(dialog);
    body.classList.remove('aph-menu-open');
    qs('[data-aph-mobile-open]')?.setAttribute('aria-expanded', 'false');
  };

  const initMobileMenu = (scope = document) => {
    const dialog = qs('[data-aph-mobile-dialog]');
    qsa('[data-aph-mobile-open]', scope).forEach((button) => {
      if (button.dataset.aphBound === 'true') return;
      button.dataset.aphBound = 'true';
      button.addEventListener('click', () => {
        if (!dialog) return;
        openDialog(dialog, button);
        body.classList.add('aph-menu-open');
        button.setAttribute('aria-expanded', 'true');
        setOverlayState();
        window.setTimeout(() => qs('[data-aph-mobile-close]', dialog)?.focus(), 20);
      });
    });

    if (dialog && dialog.dataset.aphMobileBound !== 'true') {
      dialog.dataset.aphMobileBound = 'true';
      dialog.addEventListener('close', () => {
        body.classList.remove('aph-menu-open');
        qs('[data-aph-mobile-open]')?.setAttribute('aria-expanded', 'false');
        setOverlayState();
      });
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeMobilePanel();
      });
      qsa('[data-aph-mobile-close]', dialog).forEach((button) => button.addEventListener('click', closeMobilePanel));
      qsa('[data-aph-mobile-submenu-toggle]', dialog).forEach((button) => {
        button.addEventListener('click', () => {
          const submenu = button.closest('.aph-mobile-nav__item')?.querySelector('.aph-mobile-nav__submenu');
          if (!submenu) return;
          const open = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', String(!open));
          submenu.hidden = open;
        });
      });
    }
  };

  const formatCurrency = (value, currency = 'USD', valueIsCents = true) => {
    const locale = document.documentElement.lang || 'en-US';
    let number = Number(value || 0);
    if (valueIsCents) number /= 100;
    try { return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(number); }
    catch (error) { return `$${number.toFixed(2)}`; }
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));

  const initSearch = (scope = document) => {
    const dialog = qs('[data-aph-search-dialog]');
    if (!dialog) return;

    qsa('[data-aph-search-open]', scope).forEach((button) => {
      if (button.dataset.aphSearchBound === 'true') return;
      button.dataset.aphSearchBound = 'true';
      button.addEventListener('click', () => {
        closeMobilePanel();
        openDialog(dialog, button);
        window.setTimeout(() => qs('[data-aph-search-input]', dialog)?.focus(), 30);
      });
    });

    if (dialog.dataset.aphBound === 'true') return;
    dialog.dataset.aphBound = 'true';
    const input = qs('[data-aph-search-input]', dialog);
    const form = qs('[data-aph-search-form]', dialog);
    const results = qs('[data-aph-search-results]', dialog);
    const status = qs('[data-aph-search-status]', dialog);
    let timer = null;
    let controller = null;

    qsa('[data-aph-drawer-close]', dialog).forEach((button) => button.addEventListener('click', () => closeDialog(dialog)));
    dialog.addEventListener('click', (event) => { if (event.target === dialog) closeDialog(dialog); });
    dialog.addEventListener('close', () => {
      input?.setAttribute('aria-expanded', 'false');
      setOverlayState();
    });

    const focusOptions = () => qsa('[role="option"]', results);
    const runSearch = async () => {
      const query = input?.value.trim() || '';
      if (query.length < 2) {
        if (results) results.innerHTML = '';
        if (status) status.textContent = 'Start typing to see products instantly.';
        input?.setAttribute('aria-expanded', 'false');
        return;
      }
      controller?.abort();
      controller = new AbortController();
      if (status) status.textContent = 'Searching…';
      try {
        const endpoint = dialog.dataset.searchUrl;
        const sectionId = dialog.dataset.searchSectionId || 'predictive-search';
        const params = new URLSearchParams();
        params.set('q', query);
        params.set('resources[type]', 'product');
        params.set('resources[limit]', '8');
        params.set('resources[options][unavailable_products]', 'last');
        params.set('section_id', sectionId);
        const response = await fetch(`${endpoint}?${params.toString()}`, { signal: controller.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Search request failed');
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const content = doc.querySelector('[data-aph-predictive-results]');
        if (!content) throw new Error('Search results unavailable');
        results.innerHTML = content.outerHTML;
        const count = Number(content.dataset.resultCount || 0);
        input?.setAttribute('aria-expanded', String(count > 0));
        status.textContent = `${count} product${count === 1 ? '' : 's'} found for ${query}.`;
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (results) results.innerHTML = '';
        if (status) status.textContent = 'Search is temporarily unavailable.';
        input?.setAttribute('aria-expanded', 'false');
      }
    };

    input?.addEventListener('input', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(runSearch, 180);
    });
    input?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        const options = focusOptions();
        if (options.length) { event.preventDefault(); options[0].focus(); }
      }
    });
    results?.addEventListener('keydown', (event) => {
      const options = focusOptions();
      const index = options.indexOf(document.activeElement);
      if (event.key === 'ArrowDown' && index >= 0) { event.preventDefault(); options[Math.min(options.length - 1, index + 1)]?.focus(); }
      if (event.key === 'ArrowUp' && index >= 0) {
        event.preventDefault();
        if (index === 0) input?.focus(); else options[index - 1]?.focus();
      }
    });
    form?.addEventListener('submit', (event) => {
      window.clearTimeout(timer);
      const query = input?.value.trim() || '';
      if (!query) {
        event.preventDefault();
        if (status) status.textContent = 'Type something to search the shop.';
        input?.focus();
      }
    });
  };

  const initCart = (scope = document) => {
    const sectionId = 'aph-cart-drawer';
    const currentDialog = () => qs('[data-aph-cart-dialog]');
    const jsonRoute = (url) => url?.endsWith('.js') ? url : `${url}.js`;
    const routes = () => {
      const dialog = currentDialog();
      return {
        cart: jsonRoute(dialog?.dataset.cartUrl),
        add: jsonRoute(dialog?.dataset.cartAddUrl),
        change: jsonRoute(dialog?.dataset.cartChangeUrl)
      };
    };
    const updateCounts = (count) => {
      qsa('[data-aph-cart-count]').forEach((node) => {
        node.textContent = count;
        node.hidden = count === 0;
      });
      qsa('[data-aph-cart-count-inline]').forEach((node) => node.textContent = count);
    };
    const applySection = (html, shouldOpen = true) => {
      if (!html) throw new Error('Cart section unavailable');
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incomingDialog = doc.querySelector('[data-aph-cart-dialog]');
      const dialog = currentDialog();
      if (!incomingDialog || !dialog) throw new Error('Cart drawer unavailable');
      const incomingPanel = qs('.aph-drawer__panel', incomingDialog);
      const currentPanel = qs('.aph-drawer__panel', dialog);
      if (incomingPanel && currentPanel) currentPanel.replaceWith(incomingPanel);
      const count = Number(qs('[data-aph-rendered-cart-count]', incomingDialog)?.dataset.aphRenderedCartCount || 0);
      const renderedCount = qs('[data-aph-rendered-cart-count]', dialog);
      if (renderedCount) renderedCount.dataset.aphRenderedCartCount = String(count);
      updateCounts(count);
      if (shouldOpen && !dialog.open) openDialog(dialog, dialog._adReturnFocus || document.activeElement);
      setOverlayState();
      return dialog;
    };
    const refreshDrawer = async (shouldOpen = true) => {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', sectionId);
      const response = await fetch(`${url.pathname}${url.search}`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart request failed');
      const sections = await response.json();
      return applySection(sections[sectionId], shouldOpen);
    };
    const changeItem = async (key, quantity) => {
      const route = routes().change;
      if (!route) throw new Error('Cart route unavailable');
      const dialog = currentDialog();
      dialog?.setAttribute('aria-busy', 'true');
      const response = await fetch(route, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: Number(quantity), sections: sectionId, sections_url: `${window.location.pathname}${window.location.search}` })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Cart update failed');
      applySection(data.sections?.[sectionId], true);
      currentDialog()?.removeAttribute('aria-busy');
    };

    qsa('[data-aph-cart-open]', scope).forEach((button) => {
      if (button.dataset.aphCartBound === 'true') return;
      button.dataset.aphCartBound = 'true';
      button.addEventListener('click', async () => {
        closeMobilePanel();
        const cartDialog = currentDialog();
        if (cartDialog) cartDialog._adReturnFocus = button;
        try { await refreshDrawer(true); }
        catch (error) {
          const dialog = currentDialog();
          if (dialog) {
            openDialog(dialog);
            const status = qs('[data-aph-cart-status]', dialog);
            if (status) status.textContent = 'Could not load the cart.';
          }
        }
      });
    });

    const dialog = currentDialog();
    if (dialog && dialog.dataset.aphCartBound !== 'true') {
      dialog.dataset.aphCartBound = 'true';
      dialog.addEventListener('close', setOverlayState);
      dialog.addEventListener('click', async (event) => {
        if (event.target === dialog) { closeDialog(dialog); return; }
        const close = event.target.closest('[data-aph-drawer-close],[data-aph-cart-shop]');
        if (close) { closeDialog(dialog); return; }
        const button = event.target.closest('[data-aph-cart-change]');
        if (!button) return;
        button.disabled = true;
        const status = qs('[data-aph-cart-status]', dialog);
        if (status) status.textContent = 'Updating cart…';
        try { await changeItem(button.dataset.aphCartChange, button.dataset.quantity); }
        catch (error) { if (status) status.textContent = error.message || 'Could not update the cart.'; }
        finally { button.disabled = false; dialog.removeAttribute('aria-busy'); }
      });
    }

    const addVariant = async (variantId, quantity = 1, properties = {}) => {
      const route = routes().add;
      if (!route) throw new Error('Cart route unavailable');
      const response = await fetch(route, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: Math.max(1, Number(quantity) || 1), properties }],
          sections: sectionId,
          sections_url: `${window.location.pathname}${window.location.search}`
        })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || payload.message || 'Could not add item');
      const drawer = applySection(payload.sections?.[sectionId], true);
      const status = qs('[data-aph-cart-status]', drawer);
      if (status) status.textContent = 'Item added to cart.';
      announceGlobal('Item added to cart.');
      return payload;
    };

    window.__adCartApi = { sectionId, applySection, refreshDrawer, routes, addVariant };

    if (document.documentElement.dataset.aphAjaxCartBound !== 'true') {
      document.documentElement.dataset.aphAjaxCartBound = 'true';
      document.addEventListener('submit', async (event) => {
        const form = event.target.closest('form[data-aph-cart-form]');
        if (!form || form.dataset.aphNoAjax === 'true') return;
        const api = window.__adCartApi;
        if (!api) return;
        event.preventDefault();
        const submitter = event.submitter;
        const originalLabel = submitter?.textContent;
        if (submitter) { submitter.disabled = true; submitter.setAttribute('aria-busy', 'true'); submitter.textContent = 'Adding...'; }
        form.setAttribute('aria-busy', 'true');
        try {
          const data = new FormData(form);
          data.append('sections', api.sectionId);
          data.append('sections_url', `${window.location.pathname}${window.location.search}`);
          const response = await fetch(api.routes().add, { method: 'POST', headers: { Accept: 'application/json' }, body: data });
          const payload = await response.json();
          if (!response.ok) throw new Error(payload.description || 'Could not add item');
          const dialog = api.applySection(payload.sections?.[api.sectionId], true);
          const status = qs('[data-aph-cart-status]', dialog);
          const addedMessage = `${payload.product_title || 'Item'} added to cart.`;
          if (status) status.textContent = addedMessage;
          announceGlobal(addedMessage);
        } catch (error) {
          const dialog = currentDialog();
          if (dialog) {
            openDialog(dialog);
            const status = qs('[data-aph-cart-status]', dialog);
            if (status) status.textContent = error.message || 'Could not add this item to cart.';
          }
        } finally {
          form.removeAttribute('aria-busy');
          if (submitter) { submitter.disabled = false; submitter.removeAttribute('aria-busy'); submitter.textContent = originalLabel; }
        }
      });
    }
  };

  const initProductCardNavigation = (scope = document) => {
    const hoverCapable = window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;
    qsa('[data-aph-product-card-url]', scope).forEach((card) => {
      if (card.dataset.aphCardBound === 'true') return;
      card.dataset.aphCardBound = 'true';

      const secondaryImage = qs('[data-aph-card-hover-image]', card);
      const loadSecondaryImage = () => {
        if (!secondaryImage || secondaryImage.classList.contains('is-loaded')) return;
        const source = secondaryImage.dataset.src;
        if (!source) return;
        secondaryImage.removeAttribute('data-src');
        secondaryImage.src = source;
        const reveal = () => secondaryImage.classList.add('is-loaded');
        if (typeof secondaryImage.decode === 'function') secondaryImage.decode().then(reveal).catch(reveal);
        else secondaryImage.addEventListener('load', reveal, { once: true });
      };
      if (secondaryImage && hoverCapable) {
        card.addEventListener('pointerenter', loadSecondaryImage, { once: true, passive: true });
      }
      card.addEventListener('click', (event) => {
        if (event.defaultPrevented) return;
        if (event.target.closest('a,button,input,select,textarea,label,form')) return;
        const url = card.dataset.aphProductCardUrl;
        if (url) window.location.href = url;
      });
      card.addEventListener('keydown', (event) => {
        if ((event.key !== 'Enter' && event.key !== ' ') || event.target !== card) return;
        event.preventDefault();
        const url = card.dataset.aphProductCardUrl;
        if (url) window.location.href = url;
      });
    });

    qsa('[data-aph-quick-add]', scope).forEach((button) => {
      if (button.dataset.aphQuickAddBound === 'true') return;
      button.dataset.aphQuickAddBound = 'true';
      button.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();
        const variantId = button.dataset.variantId;
        if (!variantId || button.disabled) return;
        const original = button.textContent;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = 'Adding...';
        try {
          const api = window.__adCartApi;
          if (!api?.addVariant) throw new Error('Cart is not ready yet.');
          await api.addVariant(variantId, 1);
          button.textContent = 'Added';
          announceGlobal(`${button.dataset.productTitle || 'Item'} added to cart.`);
          window.setTimeout(() => { if (button.isConnected) button.textContent = original; }, 1200);
        } catch (error) {
          button.textContent = 'Try again';
          announceGlobal(error.message || 'Could not add this item to cart.');
          const dialog = qs('[data-aph-cart-dialog]');
          if (dialog) {
            openDialog(dialog);
            const status = qs('[data-aph-cart-status]', dialog);
            if (status) status.textContent = error.message || 'Could not add this item to cart.';
          }
          window.setTimeout(() => { if (button.isConnected) button.textContent = original; }, 1800);
        } finally {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      });
    });
  };

  const initReviews = () => {};

  const initCarousels = (scope = document) => {
    qsa('[data-aph-carousel]', scope).forEach((root) => {
      if (root.dataset.aphBound === 'true') return;
      root.dataset.aphBound = 'true';
      const section = root.closest('.aph-product-carousel-section,.aph-products-section,.aph-reviews-section') || root;
      const track = qs('[data-aph-carousel-track]', root);
      if (!track) return;
      const prev = qs('[data-aph-carousel-prev]', section);
      const next = qs('[data-aph-carousel-next]', section);
      const toggle = qs('[data-aph-carousel-toggle]', section);
      const pauseIcon = qs('[data-aph-carousel-pause-icon]', toggle || section);
      const playIcon = qs('[data-aph-carousel-play-icon]', toggle || section);
      const progress = qs('[data-aph-carousel-progress]', root);
      const progressbar = qs('[data-aph-carousel-progressbar]', root);
      const timerBar = qs('[data-aph-carousel-timer]', root);
      const slides = qsa('[data-aph-carousel-slide]', track);
      const canAutoplay = root.dataset.autoplay === 'true' && motionAllowed();
      const loop = root.dataset.loop !== 'false';
      const pauseHover = root.dataset.pauseHover !== 'false';
      const interval = Math.max(3000, Number(root.dataset.interval || 5000));
      let autoTimer = null;
      let restartTimer = null;
      let userPaused = false;
      let inView = true;
      let pointerPaused = false;
      let hoverPaused = false;
      let focusPaused = false;
      let raf = 0;

      root.setAttribute('role', 'region');
      root.setAttribute('aria-roledescription', 'carousel');
      root.setAttribute('aria-label', root.dataset.carouselLabel || 'Product carousel');
      track.setAttribute('aria-live', 'off');
      slides.forEach((slide, index) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', `${index + 1} of ${slides.length}`);
      });

      const ensureScrollable = () => {
        track.style.removeProperty('grid-auto-columns');
        if (!canAutoplay || slides.length < 2) return;
        const styles = getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap || 0);
        if (track.scrollWidth > track.clientWidth + 2) return;
        const mobile = window.matchMedia('(max-width: 640px)').matches;
        const targetVisible = mobile ? 1 : Math.max(1, Math.min(3, slides.length - 1));
        const gapTotal = Math.max(0, targetVisible - 1) * gap;
        track.style.gridAutoColumns = `calc((100% - ${gapTotal}px) / ${targetVisible})`;
      };

      const metrics = () => {
        const slide = slides[0];
        if (!slide) return { step: track.clientWidth, visible: 1, maxIndex: 0 };
        const styles = getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap || 0);
        const step = slide.getBoundingClientRect().width + gap;
        const visible = Math.max(1, Math.round((track.clientWidth + gap) / step));
        return { step, visible, maxIndex: Math.max(0, slides.length - visible) };
      };
      const currentIndex = () => {
        const { step, maxIndex } = metrics();
        return Math.max(0, Math.min(maxIndex, Math.round(track.scrollLeft / Math.max(1, step))));
      };
      const resetTimerVisual = () => {
        if (!timerBar) return;
        timerBar.style.animation = 'none';
        timerBar.offsetHeight;
        if (canAutoplay && !userPaused && inView && !pointerPaused && !hoverPaused && !focusPaused) timerBar.style.animation = `aph-carousel-timer ${interval}ms linear forwards`;
      };
      const update = () => {
        raf = 0;
        const { visible, maxIndex } = metrics();
        const index = currentIndex();
        if (prev) prev.disabled = !loop && index <= 0;
        if (next) next.disabled = !loop && index >= maxIndex;
        if (progress) {
          const ratio = slides.length <= visible ? 1 : Math.min(1, (index + visible) / slides.length);
          progress.style.transform = `scaleX(${ratio})`;
        }
        if (progressbar) {
          progressbar.setAttribute('aria-valuemax', String(Math.max(1, maxIndex + 1)));
          progressbar.setAttribute('aria-valuenow', String(Math.min(maxIndex + 1, index + 1)));
        }
        root.classList.toggle('is-static', slides.length <= visible);
      };
      const scrollToIndex = (index, behavior = 'smooth') => {
        const { step, maxIndex } = metrics();
        let target = index;
        if (target > maxIndex) target = loop ? 0 : maxIndex;
        if (target < 0) target = loop ? maxIndex : 0;
        track.scrollTo({ left: target * step, behavior: motionAllowed() ? behavior : 'auto' });
      };
      const stop = () => {
        window.clearTimeout(autoTimer);
        window.clearTimeout(restartTimer);
        autoTimer = null;
        restartTimer = null;
        if (timerBar) timerBar.style.animation = 'none';
      };
      const schedule = () => {
        stop();
        if (!canAutoplay || userPaused || !inView || pointerPaused || hoverPaused || focusPaused || document.hidden || root.classList.contains('is-static')) return;
        resetTimerVisual();
        autoTimer = window.setTimeout(() => {
          scrollToIndex(currentIndex() + 1);
          schedule();
        }, interval);
      };
      const restartSoon = () => {
        stop();
        if (!canAutoplay || userPaused) return;
        restartTimer = window.setTimeout(schedule, 1800);
      };
      const setPaused = (paused) => {
        userPaused = paused;
        toggle?.setAttribute('aria-pressed', String(paused));
        if (toggle) toggle.setAttribute('aria-label', paused ? 'Play carousel' : 'Pause carousel');
        if (pauseIcon) pauseIcon.hidden = paused;
        if (playIcon) playIcon.hidden = !paused;
        root.classList.toggle('is-user-paused', paused);
        paused ? stop() : schedule();
      };
      const onVisibility = () => document.hidden ? stop() : schedule();
      const onBlockSelect = (event) => {
        const slide = event.target.closest?.('[data-aph-carousel-slide]');
        if (!slide || !root.contains(slide)) return;
        stop();
        const index = slides.indexOf(slide);
        if (index >= 0) scrollToIndex(index, 'auto');
      };
      const onBlockDeselect = (event) => { if (root.contains(event.target)) schedule(); };
      const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
        inView = entries[0]?.isIntersecting !== false;
        inView ? schedule() : stop();
      }, { threshold: .18 }) : null;
      observer?.observe(root);
      const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(() => { ensureScrollable(); update(); schedule(); }) : null;
      resizeObserver?.observe(track);

      prev?.addEventListener('click', () => { scrollToIndex(currentIndex() - 1); restartSoon(); });
      next?.addEventListener('click', () => { scrollToIndex(currentIndex() + 1); restartSoon(); });
      toggle?.addEventListener('click', () => setPaused(!userPaused));
      track.addEventListener('scroll', () => {
        if (!raf) raf = requestAnimationFrame(update);
      }, { passive: true });
      track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); scrollToIndex(currentIndex() - 1); restartSoon(); }
        if (event.key === 'ArrowRight') { event.preventDefault(); scrollToIndex(currentIndex() + 1); restartSoon(); }
        if (event.key === 'Home') { event.preventDefault(); scrollToIndex(0); restartSoon(); }
        if (event.key === 'End') { event.preventDefault(); scrollToIndex(metrics().maxIndex); restartSoon(); }
      });
      if (pauseHover) {
        root.addEventListener('mouseenter', () => { hoverPaused = true; stop(); });
        root.addEventListener('mouseleave', () => { hoverPaused = false; schedule(); });
      }
      root.addEventListener('focusin', () => { focusPaused = true; stop(); });
      root.addEventListener('focusout', (event) => { if (!root.contains(event.relatedTarget)) { focusPaused = false; schedule(); } });

      const pauseForTouch = (event) => {
        if (event.pointerType === 'mouse') return;
        pointerPaused = true;
        stop();
      };
      const resumeAfterTouch = (event) => {
        if (event.pointerType === 'mouse') return;
        pointerPaused = false;
        restartSoon();
      };
      track.addEventListener('pointerdown', pauseForTouch, { passive: true });
      track.addEventListener('pointerup', resumeAfterTouch, { passive: true });
      track.addEventListener('pointercancel', resumeAfterTouch, { passive: true });
      document.addEventListener('visibilitychange', onVisibility);
      document.addEventListener('shopify:block:select', onBlockSelect);
      document.addEventListener('shopify:block:deselect', onBlockDeselect);
      root._adDestroy = () => {
        stop();
        observer?.disconnect();
        resizeObserver?.disconnect();
        if (raf) cancelAnimationFrame(raf);
        document.removeEventListener('visibilitychange', onVisibility);
        document.removeEventListener('shopify:block:select', onBlockSelect);
        document.removeEventListener('shopify:block:deselect', onBlockDeselect);
      };
      if (!motionAllowed() && toggle) toggle.hidden = true;
      ensureScrollable();
      update();
      schedule();
    });
  };

  const initHeroSliders = (scope = document) => {
    qsa('[data-aph-hero-slider]', scope).forEach((root) => {
      if (root.dataset.aphBound === 'true') return;
      root.dataset.aphBound = 'true';
      const slides = qsa('[data-aph-hero-slide]', root);
      if (slides.length < 2) return;
      const dots = qsa('[data-aph-hero-dot]', root);
      const prev = qs('[data-aph-hero-prev]', root);
      const next = qs('[data-aph-hero-next]', root);
      const autoplay = root.dataset.autoplay === 'true' && motionAllowed();
      const interval = Math.max(3000, Number(root.dataset.interval || 5000));
      const pauseHover = root.dataset.pauseHover === 'true';
      const toggle = qs('[data-aph-hero-toggle]', root);
      const pauseIcon = qs('[data-aph-hero-pause-icon]', toggle || root);
      const playIcon = qs('[data-aph-hero-play-icon]', toggle || root);
      let userPaused = false;
      let current = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let timer = null;
      let inView = true;
      let observer = null;

      const stop = () => { if (timer) window.clearInterval(timer); timer = null; };
      const start = () => { stop(); if (autoplay && !userPaused && inView && !document.hidden) timer = window.setInterval(() => show(current + 1), interval); };
      const setPaused = (paused) => {
        userPaused = paused;
        toggle?.setAttribute('aria-pressed', String(paused));
        toggle?.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
        if (pauseIcon) pauseIcon.hidden = paused;
        if (playIcon) playIcon.hidden = !paused;
        paused ? stop() : start();
      };
      const restart = () => { if (autoplay) start(); };
      const show = (index, userInitiated = false) => {
        const nextIndex = (index + slides.length) % slides.length;
        if (nextIndex !== current) root.classList.add('has-advanced');
        current = nextIndex;
        slides.forEach((slide, i) => {
          const active = i === current;
          slide.hidden = !active;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
        });
        dots.forEach((dot, i) => {
          const active = i === current;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-selected', String(active));
        });
        if (userInitiated) restart();
      };
      const onVisibility = () => document.hidden ? stop() : start();
      const onBlockSelect = (event) => {
        const slide = event.target.closest?.('[data-aph-hero-slide]');
        if (!slide || !root.contains(slide)) return;
        stop();
        const index = slides.indexOf(slide);
        if (index >= 0) show(index, false);
      };
      const onBlockDeselect = (event) => { if (root.contains(event.target)) start(); };

      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver((entries) => {
          inView = entries[0]?.isIntersecting !== false;
          root.classList.toggle('is-offscreen', !inView);
          inView ? start() : stop();
        }, { threshold: .05, rootMargin: '160px 0px' });
        observer.observe(root);
      }

      prev?.addEventListener('click', () => show(current - 1, true));
      next?.addEventListener('click', () => show(current + 1, true));
      dots.forEach((dot) => dot.addEventListener('click', () => show(Number(dot.dataset.aphHeroDot), true)));
      if (pauseHover) {
        root.addEventListener('mouseenter', stop);
        root.addEventListener('mouseleave', start);
      }
      toggle?.addEventListener('click', () => setPaused(!userPaused));
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', (event) => { if (!root.contains(event.relatedTarget)) start(); });
      root.addEventListener('keydown', (event) => { if (event.key === 'ArrowLeft') show(current - 1, true); if (event.key === 'ArrowRight') show(current + 1, true); });
      document.addEventListener('visibilitychange', onVisibility);
      document.addEventListener('shopify:block:select', onBlockSelect);
      document.addEventListener('shopify:block:deselect', onBlockDeselect);
      let swipeStart = null;
      root.addEventListener('pointerdown', (event) => { if (event.button === undefined || event.button === 0) swipeStart = event.clientX; }, { passive: true });
      root.addEventListener('pointerup', (event) => {
        if (swipeStart === null) return;
        const delta = event.clientX - swipeStart;
        swipeStart = null;
        if (Math.abs(delta) >= 42) show(current + (delta < 0 ? 1 : -1), true);
      }, { passive: true });
      root._adDestroy = () => {
        stop();
        observer?.disconnect();
        document.removeEventListener('visibilitychange', onVisibility);
        document.removeEventListener('shopify:block:select', onBlockSelect);
        document.removeEventListener('shopify:block:deselect', onBlockDeselect);
      };
      start();
    });
  };

  const initProductPages = (scope = document) => {
    qsa('[data-aph-product-root]', scope).forEach((root) => {
      if (root.dataset.aphProductBound === 'true') return;
      root.dataset.aphProductBound = 'true';
      const productCleanupFns = [];
      const gallery = qs('[data-aph-product-gallery]', root);
      if (gallery) {
        const mediaButtons = qsa('[data-aph-product-media-target]', gallery);
        const mediaPanels = qsa('[data-aph-product-media]', gallery);
        const mediaCount = qs('[data-aph-product-media-count]', gallery);
        const mediaPrev = qs('[data-aph-product-media-prev]', gallery);
        const mediaNext = qs('[data-aph-product-media-next]', gallery);
        const thumbShell = qs('[data-aph-product-thumbs-shell]', gallery);
        const thumbTrack = qs('[data-aph-product-thumbs]', gallery);
        const thumbScrollButtons = qsa('[data-aph-thumb-scroll]', gallery);
        const mediaStage = qs('[data-aph-product-media-stage]', gallery);
        let activeIndex = Math.max(0, mediaButtons.findIndex((button) => button.classList.contains('is-active')));

        const syncThumbControls = () => {
          if (!thumbTrack) return;
          const maxScroll = Math.max(0, thumbTrack.scrollWidth - thumbTrack.clientWidth);
          const current = Math.max(0, thumbTrack.scrollLeft);
          thumbShell?.classList.toggle('has-overflow', maxScroll > 4);
          thumbScrollButtons.forEach((control) => {
            const direction = Number(control.dataset.aphThumbScroll || 0);
            control.disabled = maxScroll <= 4 || (direction < 0 ? current <= 3 : current >= maxScroll - 3);
          });
        };

        const centerActiveThumb = (button, behavior = 'smooth') => {
          if (!thumbTrack || !button) return;
          const target = button.offsetLeft - (thumbTrack.clientWidth - button.offsetWidth) / 2;
          thumbTrack.scrollTo({ left: Math.max(0, target), behavior });
          window.setTimeout(syncThumbControls, behavior === 'smooth' ? 260 : 0);
        };

        const scrollThumbs = (direction) => {
          if (!thumbTrack) return;
          const sample = mediaButtons[0];
          const step = sample ? sample.offsetWidth + 10 : Math.max(84, thumbTrack.clientWidth * .45);
          thumbTrack.scrollBy({ left: direction * step * 2, behavior: 'smooth' });
          window.setTimeout(syncThumbControls, 260);
        };

        thumbScrollButtons.forEach((control) => {
          control.addEventListener('click', () => scrollThumbs(Number(control.dataset.aphThumbScroll || 0)));
        });
        thumbTrack?.addEventListener('scroll', syncThumbControls, { passive: true });
        const onThumbResize = () => requestAnimationFrame(syncThumbControls);
        window.addEventListener('resize', onThumbResize, { passive: true });
        productCleanupFns.push(() => window.removeEventListener('resize', onThumbResize));

        const showMedia = (index, { focusThumb = true } = {}) => {
          if (!mediaButtons.length) return;
          const nextIndex = (index + mediaButtons.length) % mediaButtons.length;
          const button = mediaButtons[nextIndex];
          const id = button.dataset.aphProductMediaTarget;
          mediaPanels.forEach((media) => {
            const active = media.dataset.aphProductMedia === id;
            media.hidden = !active;
            media.classList.toggle('is-active', active);
          });
          mediaButtons.forEach((thumb) => {
            const active = thumb === button;
            thumb.classList.toggle('is-active', active);
            if (active) thumb.setAttribute('aria-current', 'true');
            else thumb.removeAttribute('aria-current');
          });
          activeIndex = nextIndex;
          if (focusThumb) centerActiveThumb(button);
          if (mediaCount) mediaCount.textContent = `${activeIndex + 1} / ${mediaButtons.length}`;
          syncThumbControls();
        };

        mediaButtons.forEach((button, index) => button.addEventListener('click', () => showMedia(index)));
        mediaPrev?.addEventListener('click', () => showMedia(activeIndex - 1));
        mediaNext?.addEventListener('click', () => showMedia(activeIndex + 1));
        if (mediaCount) mediaCount.textContent = `${activeIndex + 1} / ${mediaButtons.length}`;

        let swipeStartX = null;
        let swipeStartY = null;
        mediaStage?.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          if (event.target.closest('button,a,input,select,textarea,video,model-viewer')) return;
          swipeStartX = event.clientX;
          swipeStartY = event.clientY;
        }, { passive: true });
        mediaStage?.addEventListener('pointerup', (event) => {
          if (swipeStartX === null || swipeStartY === null) return;
          const dx = event.clientX - swipeStartX;
          const dy = event.clientY - swipeStartY;
          swipeStartX = null;
          swipeStartY = null;
          if (Math.abs(dx) < 48 || Math.abs(dx) <= Math.abs(dy) * 1.15) return;
          showMedia(activeIndex + (dx < 0 ? 1 : -1));
        }, { passive: true });
        mediaStage?.addEventListener('pointercancel', () => { swipeStartX = null; swipeStartY = null; }, { passive: true });
        requestAnimationFrame(() => {
          centerActiveThumb(mediaButtons[activeIndex], 'auto');
          syncThumbControls();
        });
      }

      const form = qs('.aph-product-form', root);
      const addButton = qs('[data-aph-add-to-cart]', root);
      const stickyBar = qs('[data-aph-sticky-buy]', root);
      const stickyButton = qs('[data-aph-sticky-submit]', root);
      const optionStatus = qs('[data-aph-option-status]', root);
      let optionController = null;

      const pickupContainer = qs('[data-aph-pickup-container]', root);
      if (pickupContainer?.dataset.variantId) {
        const loadPickup = () => {
          if (!pickupContainer.isConnected || pickupContainer.dataset.aphPickupLoaded === 'true') return;
          pickupContainer.dataset.aphPickupLoaded = 'true';
          const base = pickupContainer.dataset.rootUrl || '/';
          const pickupUrl = `${base.replace(/\/?$/, '/')}variants/${pickupContainer.dataset.variantId}/?section_id=pickup-availability`;
          fetch(pickupUrl, { headers: { Accept: 'text/html' } })
            .then((response) => response.ok ? response.text() : '')
            .then((text) => {
              if (!text || !pickupContainer.isConnected) return;
              const doc = new DOMParser().parseFromString(text, 'text/html');
              const section = qs('.shopify-section', doc);
              pickupContainer.innerHTML = section ? section.innerHTML : text;
              pickupContainer.hidden = !pickupContainer.textContent.trim();
            })
            .catch(() => { pickupContainer.hidden = true; });
        };
        runIdle(loadPickup, 900);
      }

      const captureFormState = () => {
        if (!form) return [];
        return Array.from(new FormData(form).entries()).filter(([name]) => name === 'quantity' || name.startsWith('properties['));
      };
      const restoreFormState = (newRoot, values) => {
        if (!values.length) return;
        const fields = qsa('[name]', newRoot);
        values.forEach(([name, value]) => {
          const field = fields.find((candidate) => candidate.name === name);
          if (!field || field.type === 'file') return;
          field.value = value;
        });
      };

      qsa('[data-aph-option-select]', root).forEach((select) => {
        select.addEventListener('change', async (event) => {
          const changed = event.currentTarget;
          const selectedOption = changed.options[changed.selectedIndex];
          const selects = qsa('[data-aph-option-select]', root);
          const optionValues = selects.map((field) => field.options[field.selectedIndex]?.dataset.optionValueId).filter(Boolean);
          const nextProductUrl = selectedOption?.dataset.productUrl || root.dataset.productUrl || window.location.pathname;
          const sectionId = root.dataset.sectionId;
          const savedValues = captureFormState();
          const focusPosition = changed.dataset.optionPosition;

          optionController?.abort();
          optionController = new AbortController();
          root.setAttribute('aria-busy', 'true');
          selects.forEach((field) => { field.disabled = true; });
          if (optionStatus) optionStatus.textContent = 'Updating options…';

          try {
            const requestUrl = new URL(nextProductUrl, window.location.origin);
            requestUrl.searchParams.set('section_id', sectionId);
            if (optionValues.length) requestUrl.searchParams.set('option_values', optionValues.join(','));
            const response = await fetch(requestUrl.toString(), { signal: optionController.signal, headers: { Accept: 'text/html' } });
            if (!response.ok) throw new Error('Product options could not be updated');
            const html = await response.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const incoming = qs('[data-aph-product-root]', doc);
            if (!incoming) throw new Error('Product section could not be rendered');

            const variantId = incoming.dataset.selectedVariantId;
            const browserUrl = new URL(nextProductUrl, window.location.origin);
            if (variantId) browserUrl.searchParams.set('variant', variantId);
            if (!designMode && history.replaceState) history.replaceState({}, '', `${browserUrl.pathname}${browserUrl.search}${browserUrl.hash}`);

            root._adProductDestroy?.();
            root.replaceWith(incoming);
            restoreFormState(incoming, savedValues);
            initAll(incoming.parentElement || document);
            const nextFocus = qsa('[data-aph-option-select]', incoming).find((field) => field.dataset.optionPosition === focusPosition);
            nextFocus?.focus({ preventScroll: true });
          } catch (error) {
            if (error.name === 'AbortError') return;
            root.removeAttribute('aria-busy');
            selects.forEach((field) => { field.disabled = false; });
            if (optionStatus) optionStatus.textContent = 'Please try that option again.';
          }
        });
      });

      qsa('[data-aph-quantity]', root).forEach((quantity) => {
        const input = qs('[data-aph-qty-input]', quantity);
        qs('[data-aph-qty-minus]', quantity)?.addEventListener('click', () => { input.value = Math.max(1, Number(input.value || 1) - 1); });
        qs('[data-aph-qty-plus]', quantity)?.addEventListener('click', () => { input.value = Math.max(1, Number(input.value || 1) + 1); });
      });
      stickyButton?.addEventListener('click', () => form?.requestSubmit());
      if (stickyBar && addButton && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => { const visible = !entries[0].isIntersecting && window.scrollY > 120; stickyBar.classList.toggle('is-visible', visible); body.classList.toggle('aph-sticky-buy-visible', visible); }, { threshold: .12 });
        observer.observe(addButton);
        productCleanupFns.push(() => observer.disconnect());
      }
      root._adProductDestroy = () => {
        optionController?.abort();
        productCleanupFns.forEach((cleanup) => cleanup());
      };
    });
  };

  const initProductRecommendations = (scope = document) => {
    qsa('product-recommendations[data-url]', scope).forEach((root) => {
      if (root.dataset.aphRecommendationsBound === 'true') return;
      root.dataset.aphRecommendationsBound = 'true';

      let observer = null;
      let requested = false;
      const load = () => {
        if (requested || !root.isConnected) return;
        requested = true;
        observer?.disconnect();
        fetch(root.dataset.url, { headers: { Accept: 'text/html' } }).then((response) => response.text()).then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const incoming = qs('product-recommendations', html);
          if (incoming && incoming.innerHTML.trim().length && root.isConnected) {
            root.innerHTML = incoming.innerHTML;
            initAll(root);
            document.dispatchEvent(new CustomEvent('ad:content-rendered', { detail: { root } }));
          }
        }).catch(() => {});
      };

      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) load();
        }, { rootMargin: '700px 0px', threshold: 0 });
        observer.observe(root);
      } else {
        runIdle(load, 1400);
      }

      root._adRecommendationsDestroy = () => observer?.disconnect();
    });
  };

  const initStorefrontFilters = (scope = document) => {
    qsa('[data-aph-results-section]', scope).forEach((root) => {
      if (root.dataset.aphFiltersBound === 'true') return;
      root.dataset.aphFiltersBound = 'true';
      const sectionId = root.dataset.sectionId;
      const panel = qs('[data-aph-filter-panel]', root);
      const openButton = qs('[data-aph-filter-open]', root);

      const setPanel = (open) => {
        if (!panel) return;
        panel.classList.toggle('is-open', open);
        body.classList.toggle('aph-filters-open', open);
        openButton?.setAttribute('aria-expanded', String(open));
        panel.setAttribute('aria-hidden', String(!open));
        if (open) window.setTimeout(() => qs('input,button,select,a', panel)?.focus(), 20);
        else if (openButton?.isConnected) window.setTimeout(() => openButton.focus(), 0);
      };

      openButton?.addEventListener('click', () => setPanel(true));
      qsa('[data-aph-filter-close]', root).forEach((button) => button.addEventListener('click', () => setPanel(false)));

      const replaceSection = async (targetUrl, pushState = true) => {
        if (!sectionId) return;
        const url = new URL(targetUrl, window.location.origin);
        const renderUrl = new URL(url.toString());
        renderUrl.searchParams.set('sections', sectionId);
        root.classList.add('is-loading');
        root.setAttribute('aria-busy', 'true');
        try {
          const response = await fetch(`${renderUrl.pathname}${renderUrl.search}`, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Filter request failed');
          const sections = await response.json();
          const html = sections[sectionId];
          if (!html) throw new Error('Filter section unavailable');
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const incoming = qs('[data-aph-results-section]', doc);
          if (!incoming) throw new Error('Updated results unavailable');
          root.replaceWith(incoming);
          if (pushState) window.history.pushState({}, '', `${url.pathname}${url.search}${url.hash}`);
          body.classList.remove('aph-filters-open');
          initAll(incoming);
          const count = Number(incoming.dataset.aphResultCount || 0);
          const resultStatus = qs('[data-aph-results-status]', incoming);
          if (resultStatus) resultStatus.textContent = `${count} product${count === 1 ? '' : 's'} shown.`;
          announceGlobal(`${count} product${count === 1 ? '' : 's'} shown.`);
          const toolbar = qs('[data-aph-results-focus]', incoming);
          if (toolbar) {
            if (window.scrollY > incoming.offsetTop + 160) toolbar.scrollIntoView({ block: 'start', behavior: motionAllowed() ? 'smooth' : 'auto' });
            window.setTimeout(() => toolbar.focus({ preventScroll: true }), motionAllowed() ? 260 : 0);
          }
        } catch (error) {
          window.location.assign(url.toString());
        }
      };

      qsa('.aph-filter-form,.aph-collection-sort', root).forEach((form) => {
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          const params = new URLSearchParams(new FormData(form));
          const url = new URL(form.action || window.location.href, window.location.origin);
          url.search = params.toString();
          replaceSection(url.toString());
        });
      });

      qsa('[data-aph-sort-select]', root).forEach((select) => {
        select.addEventListener('change', () => select.form?.requestSubmit());
      });

      qsa('.aph-filter-chip,.aph-filter-clear,.aph-pagination a', root).forEach((link) => {
        link.addEventListener('click', (event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          replaceSection(link.href);
        });
      });
    });

    if (document.documentElement.dataset.aphFilterHistoryBound !== 'true') {
      document.documentElement.dataset.aphFilterHistoryBound = 'true';
      window.addEventListener('popstate', () => window.location.reload());
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && body.classList.contains('aph-filters-open')) {
          const activeRoot = qs('[data-aph-results-section]');
          const activePanel = qs('[data-aph-filter-panel]', activeRoot);
          activePanel?.classList.remove('is-open');
          body.classList.remove('aph-filters-open');
          qs('[data-aph-filter-open]', activeRoot)?.setAttribute('aria-expanded', 'false');
        }
      });
    }
  };


  const initCartPage = (scope = document) => {
    qsa('.aph-cart-page__form', scope).forEach((form) => {
      if (form.dataset.aphCartPageBound === 'true') return;
      form.dataset.aphCartPageBound = 'true';

      const root = form.closest('[data-aph-cart-page-root]') || form.closest('.aph-cart-page');
      const sectionId = root?.dataset.sectionId;
      let controller = null;
      let inputTimer = null;

      const getStatus = () => qs('[data-aph-cart-page-status]', root || document);

      const setBusy = (busy) => {
        root?.toggleAttribute('aria-busy', busy);
        qsa('[data-aph-cart-page-step],[data-aph-cart-page-input],[data-aph-cart-page-remove]', root || document).forEach((control) => {
          control.disabled = busy;
        });
      };

      const updateGlobalCount = (count) => {
        qsa('[data-aph-cart-count]').forEach((node) => {
          node.textContent = count;
          node.hidden = count === 0;
        });
        qsa('[data-aph-cart-count-inline]').forEach((node) => node.textContent = count);
      };

      const renderCartPage = async (focusKey = '') => {
        if (!sectionId) {
          window.location.reload();
          return null;
        }

        const url = new URL(window.location.href);
        url.searchParams.set('section_id', sectionId);
        const response = await fetch(`${url.pathname}${url.search}`, {
          headers: { Accept: 'text/html' },
          cache: 'no-store'
        });
        if (!response.ok) throw new Error('Could not refresh cart totals');

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = qs('[data-aph-cart-page-root]', doc) || qs('.aph-cart-page', doc);
        if (!incoming || !root?.isConnected) throw new Error('Updated cart page unavailable');

        root.replaceWith(incoming);
        initAll(incoming.parentElement || document);

        if (focusKey) {
          const input = qsa('[data-aph-cart-page-input]', incoming).find((field) => field.dataset.lineKey === focusKey);
          window.setTimeout(() => input?.focus({ preventScroll: true }), 0);
        }

        return incoming;
      };

      const refreshDrawer = async () => {
        try {
          await window.__adCartApi?.refreshDrawer?.(false);
        } catch (error) {}
      };

      const changeLine = async (key, quantity, focusKey = '') => {
        if (!key) return;

        controller?.abort();
        controller = new AbortController();
        const nextQuantity = Math.max(0, Number(quantity) || 0);
        const status = getStatus();
        if (status) status.textContent = 'Updating cart…';
        setBusy(true);

        try {
          const route = window.__adCartApi?.routes?.().change || `${(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/')}cart/change.js`;
          const response = await fetch(route, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            signal: controller.signal,
            cache: 'no-store',
            body: JSON.stringify({
              id: key,
              quantity: nextQuantity
            })
          });

          const cartData = await response.json();
          if (!response.ok) throw new Error(cartData.description || cartData.message || 'Cart update failed');

          updateGlobalCount(Number(cartData.item_count || 0));
          await Promise.all([renderCartPage(nextQuantity > 0 ? focusKey : ''), refreshDrawer()]);
          announceGlobal(nextQuantity <= 0 ? 'Item removed from cart.' : 'Cart updated.');
        } catch (error) {
          if (error.name === 'AbortError') return;
          setBusy(false);
          const currentStatus = getStatus();
          if (currentStatus) currentStatus.textContent = error.message || 'Could not update the cart.';
          announceGlobal(error.message || 'Could not update the cart.');
        }
      };

      qsa('[data-aph-cart-page-step]', form).forEach((button) => {
        button.addEventListener('click', () => {
          const group = button.closest('[data-aph-cart-page-qty]');
          const input = qs('[data-aph-cart-page-input]', group);
          const key = group?.dataset.lineKey || input?.dataset.lineKey;
          if (!input || !key) return;

          const step = Number(button.dataset.aphCartPageStep || 0);
          const next = Math.max(0, Number(input.value || 0) + step);
          input.value = String(next);
          changeLine(key, next, key);
        });
      });

      qsa('[data-aph-cart-page-input]', form).forEach((input) => {
        input.addEventListener('change', () => {
          window.clearTimeout(inputTimer);
          const key = input.dataset.lineKey || input.closest('[data-aph-cart-page-qty]')?.dataset.lineKey;
          const next = Math.max(0, Number(input.value || 0));
          input.value = String(next);
          inputTimer = window.setTimeout(() => changeLine(key, next, key), 120);
        });
      });

      qsa('[data-aph-cart-page-remove]', form).forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          changeLine(link.dataset.aphCartPageRemove, 0);
        });
      });

      form.addEventListener('submit', (event) => {
        const submitter = event.submitter;
        if (!submitter || submitter.name !== 'checkout') return;
        form.setAttribute('aria-busy', 'true');
        submitter.setAttribute('aria-busy', 'true');
      });
    });
  };



  const initMoodSwitcher = (scope = document) => {
    const root = qs('[data-aph-mood-switcher]', scope) || qs('[data-aph-mood-switcher]');
    if (!root || root.dataset.aphMoodBound === 'true') return;
    root.dataset.aphMoodBound = 'true';
    const html = document.documentElement;
    const toggle = qs('[data-aph-mood-toggle]', root);
    const panel = qs('[data-aph-mood-panel]', root);
    const close = qs('[data-aph-mood-close]', root);
    const options = qsa('[data-aph-mood-option]', root);
    const valid = options.map((button) => button.dataset.aphMoodOption).filter(Boolean);

    const updateLogos = (mood) => {
      qsa('[data-aph-mood-logo]').forEach((logo) => {
        const key = `logo${mood.charAt(0).toUpperCase()}${mood.slice(1)}`;
        const next = logo.dataset[key];
        if (!next || logo.src === next || logo.getAttribute('src') === next) return;
        logo.classList.add('is-changing');
        const image = new Image();
        image.onload = () => {
          logo.src = next;
          requestAnimationFrame(() => logo.classList.remove('is-changing'));
        };
        image.onerror = () => logo.classList.remove('is-changing');
        image.src = next;
      });
    };

    const updateThemeColor = () => {
      const meta = qs('[data-aph-theme-color]');
      if (!meta) return;
      const value = getComputedStyle(html).getPropertyValue('--aph-primary').trim();
      if (value) meta.setAttribute('content', value);
    };

    const moodNames = { teal: 'Deep Teal', aqua: 'Aqua', mustard: 'Mustard', coral: 'Coral', charcoal: 'Charcoal' };
    const applyMood = (mood, persist = true, syncLogo = true) => {
      if (!valid.includes(mood)) mood = valid[0] || 'teal';
      html.dataset.aphMood = mood;
      root.dataset.aphActiveMood = mood;
      options.forEach((button) => button.setAttribute('aria-checked', String(button.dataset.aphMoodOption === mood)));
      qsa('[data-aph-mood-current]', root).forEach((label) => { label.textContent = moodNames[mood] || mood; });
      if (syncLogo) updateLogos(mood);
      updateThemeColor();
      if (persist) {
        try { localStorage.setItem('ad-color-mood', mood); } catch (error) {}
      }
      document.dispatchEvent(new CustomEvent('ad:moodchange', { detail: { mood } }));
    };

    const setOpen = (open) => {
      if (!panel || !toggle) return;
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      body.classList.toggle('aph-mood-open', open);
      if (open) window.setTimeout(() => qs('[aria-checked="true"]', panel)?.focus(), 20);
    };

    toggle?.addEventListener('click', (event) => {
      event.stopPropagation();
      setOpen(panel?.hidden !== false);
    });
    close?.addEventListener('click', () => setOpen(false));
    options.forEach((button) => button.addEventListener('click', () => {
      applyMood(button.dataset.aphMoodOption, true);
      setOpen(false);
      toggle?.focus();
    }));

    if (document.documentElement.dataset.aphMoodOutsideBound !== 'true') {
      document.documentElement.dataset.aphMoodOutsideBound = 'true';
      document.addEventListener('click', (event) => {
        const active = qs('[data-aph-mood-switcher]');
        if (!active || active.contains(event.target)) return;
        const activePanel = qs('[data-aph-mood-panel]', active);
        const activeToggle = qs('[data-aph-mood-toggle]', active);
        if (activePanel && !activePanel.hidden) {
          activePanel.hidden = true;
          activeToggle?.setAttribute('aria-expanded', 'false');
          body.classList.remove('aph-mood-open');
        }
      });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const active = qs('[data-aph-mood-switcher]');
        const activePanel = qs('[data-aph-mood-panel]', active);
        if (activePanel && !activePanel.hidden) {
          activePanel.hidden = true;
          const activeToggle = qs('[data-aph-mood-toggle]', active);
          activeToggle?.setAttribute('aria-expanded', 'false');
          activeToggle?.focus();
          body.classList.remove('aph-mood-open');
        }
      });
    }

    let initial = html.dataset.aphMood || valid[0] || 'teal';
    let storedMood = '';
    try {
      const stored = localStorage.getItem('ad-color-mood');
      if (stored && valid.includes(stored)) {
        initial = stored;
        storedMood = stored;
      }
    } catch (error) {}
    applyMood(initial, false, Boolean(storedMood));
    try {
      if (!localStorage.getItem('ad-mood-nudge-seen')) {
        window.setTimeout(() => {
          root.classList.add('is-nudging');
          window.setTimeout(() => root.classList.remove('is-nudging'), 2600);
          localStorage.setItem('ad-mood-nudge-seen', '1');
        }, 900);
      }
    } catch (error) {}
  };

  const initMotion = (scope = document) => {
    if (!body || body.classList.contains('aph-motion--off') || !motionAllowed()) return;
    const patterns = [
      ['.aph-products-section', ['.aph-section__head', '.aph-product-card']],
      ['.aph-collection-showcase-section', ['.aph-collection-showcase__head', '.aph-collection-tile']],
      ['.aph-product-carousel-section', ['.aph-carousel-head', '.aph-product-carousel__slide']],
      ['.aph-story-section', ['.aph-story__media', '.aph-story__copy']],
      ['.aph-reviews-section', ['.aph-reviews__head', '.aph-review-card', '.aph-review-footer']],
      ['.aph-newsletter', ['.aph-newsletter__copy', '.aph-newsletter__signup', '.aph-newsletter__art']],
      ['.aph-category-section', ['.aph-category']],
      ['.aph-collection-hero', ['.aph-collection-hero__copy', '.aph-collection-hero__art', '.aph-collection-hero__media']],
      ['.aph-collection-toolbar-wrap', ['.aph-collection-toolbar']],
      ['.aph-collection-products', ['.aph-product-card', '.aph-pagination']],
      ['.aph-product-main', ['.aph-product-gallery', '.aph-product-info']],
      ['.aph-product-details-band', ['.aph-product-details-band__intro', '.aph-product-accordion']],
      ['.aph-related-products', ['.aph-section__head', '.aph-product-card']],
      ['.aph-about-page', ['.aph-about-page__copy', '.aph-about-page__media', '.aph-about-fact']],
      ['.aph-faq-section', ['.aph-faq-section__head', '.aph-faq-item']],
      ['.aph-cart-table', ['tr']],
      ['.aph-basic', ['.aph-eyebrow', '.aph-heading', '.rte', '.aph-btn']]
    ];

    if (!window.__adMotionObserver && 'IntersectionObserver' in window) {
      window.__adMotionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-inview');
          window.__adMotionObserver.unobserve(entry.target);
        });
      }, { threshold: .1, rootMargin: '0px 0px -7% 0px' });
    }

    patterns.forEach(([selector, itemSelectors]) => {
      qsa(selector, scope).forEach((section) => {
        if (section.dataset.aphMotionBound === 'true') return;
        section.dataset.aphMotionBound = 'true';
        section.classList.add('aph-motion-section');
        const initialViewport = section.getBoundingClientRect().top < (window.innerHeight || 800) * 0.95;
        if (initialViewport) section.classList.add('is-inview');
        let index = 0;
        itemSelectors.forEach((itemSelector) => {
          qsa(itemSelector, section).forEach((item) => {
            if (item.classList.contains('aph-motion-item')) return;
            item.classList.add('aph-motion-item');
            item.style.setProperty('--aph-motion-delay', `${Math.min(index, 7) * 58}ms`);
            if (item.matches('.aph-story__media,.aph-product-gallery,.aph-collection-hero__copy')) item.classList.add('aph-motion-from-left');
            if (item.matches('.aph-story__copy,.aph-product-info,.aph-collection-hero__art,.aph-collection-hero__media')) item.classList.add('aph-motion-from-right');
            index += 1;
          });
        });
        if (!initialViewport && window.__adMotionObserver) window.__adMotionObserver.observe(section);
        else section.classList.add('is-inview');
      });
    });

    const parallaxSelectors = ['.aph-story__media img','.aph-newsletter__art img','.aph-category__media img','.aph-collection-hero__media img','.aph-product-media.is-active img'];
    const parallaxEnabled = body.classList.contains('aph-motion--playful') && motionAllowed() && !window.matchMedia('(pointer: coarse)').matches && !window.matchMedia('(max-width: 980px)').matches;
    if (parallaxEnabled && 'IntersectionObserver' in window) {
      if (!window.__adParallaxMedia) window.__adParallaxMedia = new Set();
      if (!window.__adParallaxActive) window.__adParallaxActive = new Set();
      if (!window.__adParallaxObserver) {
        window.__adParallaxObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const media = entry.target;
            media.classList.toggle('is-parallax-active', entry.isIntersecting);
            if (entry.isIntersecting) window.__adParallaxActive.add(media);
            else window.__adParallaxActive.delete(media);
          });
        }, { threshold: 0, rootMargin: '140px 0px' });
      }
      qsa(parallaxSelectors.join(','), scope).forEach((media) => {
        if (window.__adParallaxMedia.has(media)) return;
        media.classList.add('aph-parallax-media');
        window.__adParallaxMedia.add(media);
        window.__adParallaxObserver.observe(media);
      });
    }
    requestAnimationFrame(() => body.classList.add('aph-motion-ready'));
  };

  const initMarquees = (scope = document) => {
    qsa('[data-aph-marquee]', scope).forEach((root) => {
      if (root.dataset.aphMarqueeBound === 'true') return;
      root.dataset.aphMarqueeBound = 'true';
      const toggle = qs('[data-aph-marquee-toggle]', root);
      if (!toggle) return;
      const pauseIcon = qs('[data-aph-marquee-pause]', toggle);
      const playIcon = qs('[data-aph-marquee-play]', toggle);
      let observer = null;
      const setPaused = (paused) => {
        root.classList.toggle('is-paused', paused);
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.setAttribute('aria-label', paused ? 'Play moving text' : 'Pause moving text');
        if (pauseIcon) pauseIcon.hidden = paused;
        if (playIcon) playIcon.hidden = !paused;
      };
      toggle.addEventListener('click', () => setPaused(toggle.getAttribute('aria-pressed') !== 'true'));
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver((entries) => {
          root.classList.toggle('is-offscreen', entries[0]?.isIntersecting === false);
        }, { threshold: 0, rootMargin: '160px 0px' });
        observer.observe(root);
      }
      root._adMarqueeDestroy = () => observer?.disconnect();
      if (!motionAllowed()) setPaused(true);
    });
  };

  const initAnnouncements = (scope = document) => {
    qsa('[data-aph-announcement]', scope).forEach((root) => {
      if (root.dataset.aphAnnouncementBound === 'true') return;
      root.dataset.aphAnnouncementBound = 'true';
      const items = qsa('[data-aph-announcement-item]', root);
      if (items.length < 2) return;
      const prev = qs('[data-aph-announcement-prev]', root);
      const next = qs('[data-aph-announcement-next]', root);
      const autoplay = root.dataset.autoplay === 'true' && motionAllowed();
      const pauseHover = root.dataset.pauseHover === 'true';
      const interval = Math.max(3000, Number(root.dataset.interval || 5000));
      let current = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
      let timer = null;

      const show = (index) => {
        current = (index + items.length) % items.length;
        items.forEach((item, i) => {
          const active = i === current;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-hidden', String(!active));
        });
      };
      const stop = () => { if (timer) window.clearInterval(timer); timer = null; };
      const start = () => {
        stop();
        if (autoplay && !document.hidden) timer = window.setInterval(() => show(current + 1), interval);
      };
      const restart = () => { if (autoplay) start(); };
      const onVisibility = () => document.hidden ? stop() : start();
      const onBlockSelect = (event) => {
        const item = event.target.closest?.('[data-aph-announcement-item]');
        if (!item || !root.contains(item)) return;
        stop();
        const index = items.indexOf(item);
        if (index >= 0) show(index);
      };
      const onBlockDeselect = (event) => { if (root.contains(event.target)) start(); };

      prev?.addEventListener('click', () => { show(current - 1); restart(); });
      next?.addEventListener('click', () => { show(current + 1); restart(); });
      if (pauseHover) {
        root.addEventListener('mouseenter', stop);
        root.addEventListener('mouseleave', start);
      }
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', (event) => { if (!root.contains(event.relatedTarget)) start(); });
      document.addEventListener('visibilitychange', onVisibility);
      document.addEventListener('shopify:block:select', onBlockSelect);
      document.addEventListener('shopify:block:deselect', onBlockDeselect);
      root._adAnnouncementDestroy = () => {
        stop();
        document.removeEventListener('visibilitychange', onVisibility);
        document.removeEventListener('shopify:block:select', onBlockSelect);
        document.removeEventListener('shopify:block:deselect', onBlockDeselect);
      };
      show(current);
      start();
    });
  };

  const initFlow = () => {
    if (!body?.classList.contains('aph-section-flow')) return;
    qsa('#MainContent > .shopify-section').forEach((section, index) => {
      section.classList.add('aph-flow-wrapper');
      section.style.setProperty('--aph-flow-index', index);
    });
  };

  const initScrollExperience = () => {
    if (!body) return;

    const ensureHeaderSection = () => {
      const currentHeader = qs('[data-aph-header]');
      const section = currentHeader?.closest('.shopify-section');
      if (section) section.classList.add('aph-header-section');
      return currentHeader;
    };

    ensureHeaderSection();
    if (body.dataset.aphScrollBound === 'true') return;
    body.dataset.aphScrollBound = 'true';
    let lastY = Math.max(0, window.scrollY);
    let upTravel = 0;
    let downTravel = 0;
    let ticking = false;

    const update = () => {
      ticking = false;
      const y = Math.max(0, window.scrollY);
      const delta = y - lastY;
      const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      body.style.setProperty('--aph-scroll-progress', `${Math.min(100, Math.max(0, y / max * 100)).toFixed(2)}%`);
      body.classList.toggle('aph-page-scrolled', y > 36);

      const header = ensureHeaderSection();
      if (header && body.classList.contains('aph-smart-header')) {
        const blocked = body.classList.contains('aph-overlay-open') || body.classList.contains('aph-menu-open');
        if (delta > 0) { downTravel += delta; upTravel = 0; }
        else if (delta < 0) { upTravel += Math.abs(delta); downTravel = 0; }
        if (blocked || y < 110 || upTravel >= 42) body.classList.remove('aph-header-hidden');
        else if (y > 210 && downTravel >= 92) body.classList.add('aph-header-hidden');
      }

      if (body.classList.contains('aph-motion--playful') && motionAllowed() && window.__adParallaxActive?.size) {
        const vh = window.innerHeight || 1;
        window.__adParallaxActive.forEach((media) => {
          if (!media.isConnected) {
            window.__adParallaxActive.delete(media);
            window.__adParallaxMedia?.delete(media);
            window.__adParallaxObserver?.unobserve(media);
            return;
          }
          const rect = media.getBoundingClientRect();
          const center = rect.top + rect.height / 2;
          const normalized = (center - vh / 2) / vh;
          media.style.setProperty('--aph-parallax-y', `${Math.max(-12, Math.min(12, normalized * -18)).toFixed(2)}px`);
        });
      }
      lastY = y;
    };

    const requestUpdate = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    update();
  };

  const initAll = (scope = document) => {
    initMoodSwitcher(scope);
    initNavigation(scope);
    initMobileMenu(scope);
    initSearch(scope);
    initCart(scope);
    initProductCardNavigation(scope);
    initReviews(scope);
    initCarousels(scope);
    initMarquees(scope);
    initAnnouncements(scope);
    initHeroSliders(scope);
    initProductPages(scope);
    initProductRecommendations(scope);
    initStorefrontFilters(scope);
    initCartPage(scope);
    initMotion(scope);
    initFlow();
    initScrollExperience();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    qsa('[data-aph-carousel],[data-aph-hero-slider],[data-aph-product-root],[data-aph-announcement],[data-aph-marquee],product-recommendations[data-url]', event.target).forEach((node) => {
      node._adDestroy?.();
      node._adProductDestroy?.();
      node._adAnnouncementDestroy?.();
      node._adMarqueeDestroy?.();
      node._adRecommendationsDestroy?.();
    });
  });
})();
